import { useEffect, useRef, useState } from "react";

// Loads once the element scrolls near the viewport, so a long grid only fetches what is on screen; a new key clears and reloads
export function useInViewLoad<T>(key: string, load: () => Promise<T>) {
  const ref = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<{ loaded: boolean; data: T | null }>({ loaded: false, data: null });
  // Read through a ref so a caller's inline arrow doesn't reload on every render
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    // Drop the previous key's data, so a changed source never shows the old reading while the new one loads
    setState((prev) => (prev.loaded || prev.data !== null ? { loaded: false, data: null } : prev));
    const el = ref.current;
    if (!el) return;
    let cancelled = false;
    const run = () =>
      loadRef
        .current()
        .then((data) => !cancelled && setState({ loaded: true, data }))
        .catch(() => !cancelled && setState({ loaded: true, data: null }));
    // No observer (old browser, jsdom): load straight away rather than never
    if (typeof IntersectionObserver === "undefined") {
      run();
      return () => {
        cancelled = true;
      };
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        run();
      },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [key]);

  return { ref, ...state };
}
